import * as React from 'react';
import { useEffect } from 'react';

import { useApolloClient } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { graphql, useLazyLoadQuery } from 'react-relay';
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  GET_LEADS,
  GET_LEADS_COUNT,
} from '../../apollo/pages/leads/leadsQueries';
import { useLocale } from '../hooks/locale';
import { LeadDetails, useLeadTitle } from '../routes/LeadDetails/lead-details';
import { Leads } from '../routes/Leads/Leads';
import { TopbarTitle } from '../shared/topbar';

import type { LeadsRoutesDetailsQuery } from './__generated__/LeadsRoutesDetailsQuery.graphql';

interface LeadsWithDataProps {
  setTitle?: (title: string) => void;
}

export const LeadWithData = ({ setTitle }: LeadsWithDataProps) => {
  const { leadId } = useParams() as { leadId: string };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apolloClient = useApolloClient();

  const data = useLazyLoadQuery<LeadsRoutesDetailsQuery>(
    graphql`
      query LeadsRoutesDetailsQuery($leadId: ID!) {
        ...leadDetails_root
        leadById(id: $leadId) {
          ...leadDetails_lead
          ...leadDetails_leadClaimData
          userCanViewLeadDetails
          isVisibilityLead
        }
      }
    `,
    {
      leadId,
    },
  );

  const title = useLeadTitle(data.leadById);

  useEffect(() => {
    if (
      data.leadById?.userCanViewLeadDetails === false &&
      data.leadById?.isVisibilityLead === true
    ) {
      // until we finish implementing the non exxlucisve lead feature, we redirect to the leads list if the lead is visibility lead and user cannot view lead details
      navigate({
        pathname: '../',
      });
    } else {
      setTitle?.(title);
    }
  }, [
    title,
    setTitle,
    data.leadById?.userCanViewLeadDetails,
    data.leadById?.isVisibilityLead,
    navigate,
  ]);

  return (
    <>
      {setTitle != null ? (
        <TopbarTitle titleOverride={title}>
          {/* So that we don't display title in the app bar */}
          {null}
        </TopbarTitle>
      ) : (
        <TopbarTitle>{title}</TopbarTitle>
      )}
      {data.leadById != null && (
        <React.Suspense fallback={null}>
          <LeadDetails
            root={data}
            lead={data.leadById}
            closeLead={(forceRefresh = false) => {
              if (forceRefresh) {
                searchParams.set('refresh', 'true');
                apolloClient.refetchQueries({
                  include: [GET_LEADS, GET_LEADS_COUNT],
                });
              }
              navigate({
                pathname: '../',
                search: searchParams.toString(),
              });
            }}
          />
        </React.Suspense>
      )}
    </>
  );
};

export const LeadsRoutes = () => {
  const { t } = useLocale();
  return (
    <Routes>
      <Route
        index
        element={
          <React.Suspense
            fallback={
              <>
                <TopbarTitle>{t('Leads')}</TopbarTitle>
                <CircularProgress css={{ margin: 'auto' }} disableShrink />
              </>
            }
          >
            <Leads />
          </React.Suspense>
        }
      />
      <Route
        path="add/*"
        element={
          <React.Suspense
            fallback={
              <>
                <TopbarTitle>{t('Leads')}</TopbarTitle>
                <CircularProgress css={{ margin: 'auto' }} disableShrink />
              </>
            }
          >
            <Leads />
          </React.Suspense>
        }
      />
      <Route path=":leadId" element={<LeadWithData />} />
    </Routes>
  );
};

export default LeadsRoutes;
